/**
 * The loyalty stamp band: one full-width strip in the design's brand colour
 * that carries progress toward the next reward. Apple artwork, the Google hero,
 * the manager preview and the Collected card all lay it out from this module so
 * every surface shows the same slots.
 *
 * The band exists because merchant artwork can be anything. Contrast comes
 * from a surface we paint (brand background + readable ink), never from the
 * photograph underneath. See docs/plans/wallet_passes/09-stamp-band.md
 */

/** Above this threshold individual slots become noise; the band shows a bar. */
export const WALLET_STAMP_BAND_MAX_SLOTS = 12;

/** Slot rows wrap once the band would hold more than this many tokens. */
const SINGLE_ROW_MAX_TOKENS = 7;

export type WalletStampProgress = {
  /** Stamp slots before the reward slot — the reward threshold. */
  required: number;
  /** Earned slots, never more than `required`. */
  filled: number;
  /** The reward slot is earned. */
  ready: boolean;
  /** Whole rewards the balance covers; shown as ×N once above one. */
  rewardsReady: number;
  mode: "slots" | "bar";
  /** Tokens per row in `slots` mode, reward slot included in the last row. */
  rows: number[];
};

export const walletStampProgress = (
  balance: number,
  required: number,
): WalletStampProgress => {
  const target = Math.max(1, Math.floor(Number(required) || 1));
  const held = Math.max(0, Math.floor(Number(balance) || 0));
  const mode = target > WALLET_STAMP_BAND_MAX_SLOTS ? "bar" : "slots";
  const tokens = target + 1;
  const rowCount = mode === "bar" ? 0 : tokens > SINGLE_ROW_MAX_TOKENS ? 2 : 1;
  const perRow = rowCount ? Math.ceil(tokens / rowCount) : 0;
  return {
    required: target,
    filled: Math.min(held, target),
    ready: held >= target,
    rewardsReady: Math.floor(held / target),
    mode,
    rows: Array.from({ length: rowCount }, (_, row) =>
      Math.min(perRow, tokens - row * perRow),
    ),
  };
};

const channel = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a `#rrggbb` colour; null when unparseable. */
export const walletColorLuminance = (hex: string): number | null => {
  const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!match) return null;
  const [r, g, b] = match.slice(1).map((part) => channel(parseInt(part, 16)));
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
};

export const walletContrastRatio = (a: string, b: string): number | null => {
  const la = walletColorLuminance(a);
  const lb = walletColorLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** Minimum contrast between band and tokens — WCAG's large-graphics level. */
const MIN_STAMP_CONTRAST = 3;

/**
 * The ink for tokens on the band: the design's foreground when it contrasts
 * with the band colour, otherwise black or white — whichever reads.
 */
export const walletStampInk = (background: string, foreground: string) => {
  const ratio = walletContrastRatio(background, foreground);
  if (ratio !== null && ratio >= MIN_STAMP_CONTRAST) return foreground;
  const luminance = walletColorLuminance(background) ?? 0;
  return luminance > 0.18 ? "#111111" : "#FFFFFF";
};
